import { useCallback, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { CeleryTaskStatusResponse } from '@/api/generalApi'
import { extractErrorMessage } from '@/api/helpers/extractErrorMessage'
import { useWaitForCeleryTaskResultsAsync } from '@/hooks/useWaitForCeleryTaskResultsAsync'
import { useTestAiBridge, useTestAiBridgePath } from '@/queries/aiBridgeQueries'
import { AiBridgeCheckResult } from '@/types/aiBridge'

// `notFound` is kept apart from `failed` so an absent file is never reported as an
// unreachable device. It is unreachable today — see parseCheckResult — but the branch
// costs nothing and becomes live as soon as the task forwards the device's answer.
export type AiBridgeTestStatus = 'passed' | 'notFound' | 'failed'

export type AiBridgeTestResult = {
  status: AiBridgeTestStatus
  message: string
}

export const AI_BRIDGE_TEST_RESULT_COLOR: Record<AiBridgeTestStatus, string> = {
  passed: 'green.7',
  notFound: 'yellow.8',
  failed: 'red.7'
}

// test_ai_bridge_task discards the RPC payload and the status endpoint returns
// `str(task_result)`, so a passing check arrives as the string "None". Accept a
// structured result if one ever shows up, and read nothing into it when it doesn't.
const parseCheckResult = (raw: unknown): AiBridgeCheckResult | null => {
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as AiBridgeCheckResult
    } catch {
      return null
    }
  }

  return typeof raw === 'object' && raw !== null
    ? (raw as AiBridgeCheckResult)
    : null
}

export const useAiBridgeTest = () => {
  const intl = useIntl()
  const [result, setResult] = useState<AiBridgeTestResult | null>(null)

  const { mutateAsync: testAiBridgePath, isPending: isDispatchingPath } =
    useTestAiBridgePath()

  const { mutateAsync: testAiBridge, isPending: isDispatchingBridge } =
    useTestAiBridge()

  const { waitForCeleryTaskResultsAsync, isPending: isPolling } =
    useWaitForCeleryTaskResultsAsync()

  const resetResult = useCallback(() => setResult(null), [])

  // Returns the outcome as well as storing it, so callers can log or toast it.
  const runTest = useCallback(
    async (
      dispatch: () => Promise<{ task_id: string }>
    ): Promise<AiBridgeTestResult> => {
      setResult(null)

      let outcome: AiBridgeTestResult

      try {
        const { task_id } = await dispatch()
        const taskResponse = await waitForCeleryTaskResultsAsync(task_id)
        const checkResult = parseCheckResult(taskResponse.task_result)
        const isMissing = !!checkResult && !checkResult.exists

        // The task raises on a failed check, so getting here without a readable
        // answer still means the device ran the check and reported no error.
        outcome = {
          status: isMissing ? 'notFound' : 'passed',
          message: intl.formatMessage({
            id: isMissing ? 'aiBridge.test.notFound' : 'aiBridge.test.passed'
          })
        }
      } catch (err) {
        // A failed Celery task rejects with the task response, not an Axios error.
        const taskResult = (err as CeleryTaskStatusResponse | undefined)
          ?.task_result

        const fallback = intl.formatMessage({ id: 'aiBridge.test.failed' })

        outcome = {
          status: 'failed',
          message:
            typeof taskResult === 'string' && taskResult !== 'None'
              ? taskResult
              : extractErrorMessage(err, fallback)
        }
      }

      setResult(outcome)

      return outcome
    },
    [waitForCeleryTaskResultsAsync, intl]
  )

  const testPath = useCallback(
    (params: { deviceUuid: string; path: string }) =>
      runTest(() => testAiBridgePath(params)),
    [runTest, testAiBridgePath]
  )

  const testBridge = useCallback(
    (bridgeId: string) => runTest(() => testAiBridge(bridgeId)),
    [runTest, testAiBridge]
  )

  return useMemo(
    () => ({
      isTesting: isDispatchingPath || isDispatchingBridge || isPolling,
      result,
      resetResult,
      testPath,
      testBridge
    }),
    [
      isDispatchingPath,
      isDispatchingBridge,
      isPolling,
      result,
      resetResult,
      testPath,
      testBridge
    ]
  )
}
